// DocumentTemplateForm.js - Skapa/redigera dokumentmall (Spår D6) med variabellista.
import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiClient from '../../apiClient';
import { UserContext } from '../../UserContext';
import { isGlobalAdmin } from '../../utils/permissions';
import '../FormTemplates/FormTemplates.css';

const CATEGORIES = [
  { value: 'intyg', label: 'Intyg' },
  { value: 'remiss', label: 'Remiss' },
  { value: 'brev', label: 'Brev' },
  { value: 'ovrigt', label: 'Övrigt' },
];

function DocumentTemplateForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const isEditing = !!id;

  const [name, setName] = useState('');
  const [category, setCategory] = useState('ovrigt');
  const [body, setBody] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isGlobal, setIsGlobal] = useState(false);
  const [canEdit, setCanEdit] = useState(true);
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const bodyRef = useRef(null);
  // Markörposition att återställa efter att en variabel infogats (sätts efter rendering,
  // annars flyttar Reacts värdesättning markören)
  const pendingCursor = useRef(null);

  const readOnly = isEditing && !canEdit;

  useEffect(() => {
    apiClient.get('/api/document-templates/variables')
      .then(res => setVariables(res.data.variables || []))
      .catch(err => console.error('Kunde inte hämta variabler:', err));
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    apiClient.get(`/api/document-templates/${id}`)
      .then(res => {
        const t = res.data;
        setName(t.name || '');
        setCategory(t.category || 'ovrigt');
        setBody(t.body || '');
        setIsActive(!!t.is_active);
        setIsGlobal(!!t.is_global);
        setCanEdit(!!t.can_edit);
      })
      .catch(err => {
        console.error('Kunde inte hämta mallen:', err);
        toast.error(err.response?.data?.error || 'Kunde inte hämta mallen');
        navigate('/dokumentmallar');
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  /** Infoga variabel vid markören i malltexten. */
  const insertVariable = (variable) => {
    const el = bodyRef.current;
    if (!el || readOnly) return;
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    pendingCursor.current = start + variable.length;
    setBody(body.slice(0, start) + variable + body.slice(end));
  };

  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (el && pendingCursor.current !== null) {
      el.focus();
      el.setSelectionRange(pendingCursor.current, pendingCursor.current);
      pendingCursor.current = null;
    }
  }, [body]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    try {
      setSaving(true);
      setError('');
      const payload = { name, category, body, is_active: isActive };
      if (isEditing) {
        await apiClient.put(`/api/document-templates/${id}`, payload);
        toast.success('Mallen är sparad');
      } else {
        await apiClient.post('/api/document-templates', { ...payload, is_global: isGlobal });
        toast.success('Mallen är skapad');
      }
      navigate('/dokumentmallar');
    } catch (err) {
      console.error('Kunde inte spara mallen:', err);
      setError(err.response?.data?.error || 'Kunde inte spara mallen');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="ft-form-container"><p className="ft-muted">Hämtar mallen…</p></div>;

  const groups = variables.reduce((acc, v) => {
    (acc[v.group] = acc[v.group] || []).push(v);
    return acc;
  }, {});

  return (
    <div className="ft-form-container">
      <div className="ft-form-header">
        <button type="button" className="ft-btn" onClick={() => navigate('/dokumentmallar')}>
          <i className="fas fa-arrow-left"></i> Tillbaka
        </button>
        <h2>
          <i className="fas fa-file-signature"></i>{' '}
          {readOnly ? 'Visa dokumentmall' : isEditing ? 'Redigera dokumentmall' : 'Ny dokumentmall'}
        </h2>
      </div>

      {readOnly && (
        <p className="ft-notice">
          <i className="fas fa-lock"></i> Global mall — skrivskyddad. Kopiera den från listan för att anpassa.
        </p>
      )}

      <form onSubmit={handleSubmit}>
        <fieldset disabled={readOnly} className="ft-fieldset">
          <section className="ft-panel">
            <div className="ft-field-row">
              <div className="ft-field">
                <label htmlFor="dt-name">Namn</label>
                <input id="dt-name" type="text" value={name} maxLength={255} required
                  onChange={e => setName(e.target.value)} placeholder="T.ex. Intyg om sjukgymnastisk behandling" />
              </div>
              <div className="ft-field">
                <label htmlFor="dt-category">Kategori</label>
                <select id="dt-category" value={category} onChange={e => setCategory(e.target.value)}>
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
            </div>

            <div className="ft-field">
              <label htmlFor="dt-body">Malltext</label>
              <textarea id="dt-body" ref={bodyRef} rows={18} value={body} required
                onChange={e => setBody(e.target.value)}
                placeholder="Skriv mallens text. Variabler som {patientnamn} fylls i när dokumentet skapas."
                style={{ fontFamily: 'inherit', lineHeight: 1.5 }} />
              <p className="ft-hint">Tom rad = nytt stycke i PDF:en. Text inom [hakparentes] är bra som platshållare att fylla i.</p>
            </div>

            {variables.length > 0 && (
              <div className="ft-field">
                <label>Variabler {readOnly ? '' : '(klicka för att infoga)'}</label>
                {Object.entries(groups).map(([group, list]) => (
                  <div key={group} style={{ marginBottom: 6 }}>
                    <span className="ft-hint" style={{ marginRight: 8 }}>{group}:</span>
                    {list.map(v => (
                      <button key={v.variable} type="button" className="ft-btn ft-btn-sm"
                        style={{ margin: '0 6px 6px 0' }} title={`${v.description} (t.ex. ${v.example})`}
                        onMouseDown={e => e.preventDefault()} onClick={() => insertVariable(v.variable)}>
                        {v.variable}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}

            <div className="ft-field-row">
              <label className="ft-check">
                <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
                Aktiv (kan väljas när dokument skrivs)
              </label>
              {!isEditing && isGlobalAdmin(user) && (
                <label className="ft-check">
                  <input type="checkbox" checked={isGlobal} onChange={e => setIsGlobal(e.target.checked)} />
                  Global mall (för alla kliniker)
                </label>
              )}
            </div>
          </section>

          {error && <div className="ft-error-box"><i className="fas fa-exclamation-circle"></i> {error}</div>}

          {!readOnly && (
            <div className="ft-form-actions">
              <button type="button" className="ft-btn" onClick={() => navigate('/dokumentmallar')}>Avbryt</button>
              <button type="submit" className="ft-btn ft-btn-primary" disabled={saving}>
                {saving ? <><i className="fas fa-spinner fa-spin"></i> Sparar…</> : (isEditing ? 'Spara ändringar' : 'Skapa mall')}
              </button>
            </div>
          )}
        </fieldset>
      </form>
    </div>
  );
}

export default DocumentTemplateForm;
